import React from "react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

const BRAND_TERRACOTTA = "hsl(var(--brand-terracotta))";
const BRAND_TERRACOTTA_25 = "hsl(var(--brand-terracotta) / 0.25)";
const BRAND_TERRACOTTA_18 = "hsl(var(--brand-terracotta) / 0.18)";

/**
 * SVG-based floor map.
 *
 * Props:
 *  - tables: [{id,label,capacity,x,y,shape}]
 *  - assignedTableId: id of the user's table (pulsing marker)
 *  - selectedTableId: highlighted id (admin/host)
 *  - onTableClick(table)
 *  - busyTableIds: Iterable of table ids currently occupied
 *  - mode: "customer" | "host" | "admin"
 */
export default function FloorMapSvg({
    tables = [],
    assignedTableId,
    selectedTableId,
    onTableClick,
    busyTableIds = [],
    mode = "customer",
}) {
    const busy = new Set(busyTableIds || []);
    return (
        <div className="relative rounded-lg border border-border bg-card overflow-hidden" data-testid="floor-map">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full aspect-[2/1.2] block">
                <FloorBackground />
                <EntranceLabel />
                {tables.map((t) => (
                    <TableElement
                        key={t.id}
                        table={t}
                        isAssigned={t.id === assignedTableId}
                        isSelected={t.id === selectedTableId}
                        isBusy={busy.has(t.id) && t.id !== assignedTableId}
                        onClick={onTableClick}
                    />
                ))}
            </svg>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Visual helpers                                                     */
/* ------------------------------------------------------------------ */

function FloorBackground() {
    return (
        <>
            <defs>
                <pattern id="floor-pattern" patternUnits="userSpaceOnUse" width="4" height="4">
                    <path d="M 4 0 L 0 0 0 4" fill="none" stroke="hsl(var(--border))" strokeWidth="0.15" />
                </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#floor-pattern)" />
        </>
    );
}

function EntranceLabel() {
    return (
        <g>
            <rect x="45" y="96" width="10" height="4" fill="hsl(var(--secondary))" />
            <text x="50" y="99" textAnchor="middle" fontSize="1.6" fill="hsl(var(--muted-foreground))">
                ENTRANCE
            </text>
        </g>
    );
}

function getTableSize(shape) {
    if (shape === "rect") return { w: 12, h: 6 };
    return { w: 7, h: 7 };
}

function getTableStyles({ isAssigned, isSelected, isBusy }) {
    if (isAssigned) {
        return { fill: BRAND_TERRACOTTA_25, stroke: BRAND_TERRACOTTA, strokeWidth: 0.6, opacity: 1 };
    }
    if (isSelected) {
        return { fill: BRAND_TERRACOTTA_18, stroke: BRAND_TERRACOTTA, strokeWidth: 0.5, opacity: 1 };
    }
    if (isBusy) {
        return { fill: "hsl(var(--muted))", stroke: "hsl(var(--border))", strokeWidth: 0.3, opacity: 0.7 };
    }
    return { fill: "hsl(var(--secondary))", stroke: "hsl(var(--border))", strokeWidth: 0.3, opacity: 1 };
}

function TableShape({ shape, cx, cy, size, fill, stroke, strokeWidth }) {
    if (shape === "circle") {
        return <circle cx={cx} cy={cy} r={size.w / 2} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
    }
    return (
        <rect
            x={cx - size.w / 2}
            y={cy - size.h / 2}
            width={size.w}
            height={size.h}
            rx="1"
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
        />
    );
}

function AssignedMarker({ cx, cy, size }) {
    return (
        <>
            <circle
                cx={cx}
                cy={cy}
                r={size.w / 2 + 1}
                fill="hsl(var(--brand-terracotta) / 0.6)"
                className="pulse-ring"
            />
            <circle cx={cx} cy={cy} r={1} fill={BRAND_TERRACOTTA} />
        </>
    );
}

function TableElement({ table, isAssigned, isSelected, isBusy, onClick }) {
    const size = getTableSize(table.shape);
    const { fill, stroke, strokeWidth, opacity } = getTableStyles({ isAssigned, isSelected, isBusy });
    const cx = table.x;
    const cy = table.y;
    const handleClick = () => onClick?.(table);
    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            onClick?.(table);
        }
    };
    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <g
                        role="button"
                        tabIndex={0}
                        data-testid={isAssigned ? "floor-map-assigned-table" : "floor-map-table"}
                        onClick={handleClick}
                        onKeyDown={handleKeyDown}
                        style={{ cursor: "pointer", opacity }}
                    >
                        <TableShape
                            shape={table.shape}
                            cx={cx}
                            cy={cy}
                            size={size}
                            fill={fill}
                            stroke={stroke}
                            strokeWidth={strokeWidth}
                        />
                        <text x={cx} y={cy + 0.7} textAnchor="middle" fontSize="2.1" fontWeight="600" fill="hsl(var(--foreground))">
                            {table.label}
                        </text>
                        <text x={cx} y={cy + 3.4} textAnchor="middle" fontSize="1.5" fill="hsl(var(--muted-foreground))">
                            {table.capacity}p
                        </text>
                        {isAssigned && <AssignedMarker cx={cx} cy={cy} size={size} />}
                    </g>
                </TooltipTrigger>
                <TooltipContent>
                    <div className="text-xs">
                        <div className="font-medium">Table {table.label}</div>
                        <div>Seats {table.capacity}</div>
                        <div className="text-muted-foreground capitalize">
                            {isAssigned ? "Your table" : isBusy ? "Occupied" : "Available"}
                        </div>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
